import { Fragment } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import { t } from '../../localization/t';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { getClass } from '../../utils/arena/classes';
import { ALCHEMIST_POTENCY, alchemistPerksSpent } from '../../utils/arena/alchemist';
import { positionTooltip, TOOLTIP_H_EST, getCanvasScaleNow } from './positionTooltip';

const TOTAL_PERKS = ALCHEMIST_POTENCY[ALCHEMIST_POTENCY.length - 1].perks;
const PERKS_PER_TIER = ALCHEMIST_POTENCY[0].perks;
// Earned milestones wear the class accent like a learned skill. The rest
// stay locked: nothing here is bought, so nothing may look buyable.
const NODE_STATE = { earned: 'purchased', next: 'locked', future: 'locked' };
// The plate sits on the bottom edge, so the tooltip rises above it.
const ABOVE = { above: true };

function TierTooltip({ cls, tier, status, anchorRect }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(() => positionTooltip(anchorRect, TOOLTIP_H_EST * getCanvasScaleNow(), null, ABOVE));

  useLayoutEffect(() => {
    if (!anchorRect || !ref.current) return;
    const { height, width } = ref.current.getBoundingClientRect();
    if (height > 0) setPos(positionTooltip(anchorRect, height, width, ABOVE));
  }, [anchorRect]);

  if (!pos) return null;
  return (
    <div style={{ position: 'fixed', left: pos.left, top: pos.top, pointerEvents: 'none', zIndex: 200 }}>
      <motion.div
        ref={ref}
        role="tooltip"
        id={`potency-detail-${tier.perks}`}
        initial={{ opacity: 0, scale: 0.96, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 4 }}
        transition={{ duration: 0.16, ease: [0.22, 0.8, 0.36, 1] }}
        className="vh-skill-tooltip vh-class-tooltip"
        style={{
          position: 'relative',
          '--vh-tooltip-accent': cls.accent,
          '--vh-tooltip-accent-glow': `${cls.accent}b3`,
          '--vh-tooltip-glow': `${cls.accent}33`,
          '--vh-tooltip-medallion-svg': `url("${cls.majorOrnament}")`,
        }}
      >
        <div className="vh-skill-tooltip-medallion" aria-hidden="true" />
        <div className="vh-skill-tooltip-accent" />
        <div className="vh-skill-tooltip-path" style={{ color: cls.accent }}>{t('Potion Potency')} · {status}</div>
        <div className="vh-skill-tooltip-title-row">
          <h3 className="vh-skill-tooltip-title arena-heading">{t(tier.title)}</h3>
        </div>
        <div className="vh-skill-tooltip-divider" />
        <p className="vh-skill-tooltip-body">
          {t('Unlocks stronger potions for a single match or booster. Existing potions keep their potency.')}
        </p>
        <div className="vh-class-tooltip-note">
          {t('Unlocks automatically at {n} learned perks. No extra skill point required.', { n: tier.perks })}
        </div>
      </motion.div>
    </div>
  );
}

export default function AlchemistPotency({ skillState }) {
  const cls = getClass('alchemist');
  const spent = alchemistPerksSpent(skillState);
  const next = ALCHEMIST_POTENCY.find(tier => spent < tier.perks);
  const earned = ALCHEMIST_POTENCY.filter(tier => spent >= tier.perks).length;
  const previousEarned = useRef(earned);
  const [flare, setFlare] = useState(null);
  const [detail, setDetail] = useState(null); // { index, rect }
  useEffect(() => {
    if (earned > previousEarned.current) setFlare(earned - 1);
    previousEarned.current = earned;
  }, [earned]);
  const progress = Math.min(spent, TOTAL_PERKS);
  const show = (index, e) => setDetail({ index, rect: e.currentTarget.getBoundingClientRect() });
  const statusOf = tier => (spent >= tier.perks ? t('Attained') : next === tier ? t('Next reward') : t('Locked'));

  return (
    <div className="vh-potency-slot">
      <section className="vh-potency-plate" aria-label={t('Potion Potency')} data-testid="alchemist-potency"
        style={{ '--vh-const-accent': cls.accent }}>
        <div className="vh-potency-lead">
          <div className="vh-skill-points-hud-label">{t('Potion Potency')}</div>
          <div className="vh-skill-points-hud-meta-line tabular-nums">
            {t('{n} / {total} perks learned', { n: progress, total: next?.perks || TOTAL_PERKS })}
          </div>
          <div className="vh-skill-points-hud-meta-sub" aria-live="polite">
            {next ? (next.perks - spent === 1
              ? t('1 more perk to {tier}', { tier: t(next.title) })
              : t('{n} more perks to {tier}', { n: next.perks - spent, tier: t(next.title) }))
              : t('All potency tiers attained')}
          </div>
        </div>
        <div className="vh-potency-rail" role="progressbar" aria-label={t('Potion potency progress')}
          aria-valuemin={0} aria-valuemax={TOTAL_PERKS} aria-valuenow={progress}>
          {ALCHEMIST_POTENCY.map((tier, i) => {
            const unlocked = spent >= tier.perks;
            const state = unlocked ? 'earned' : next === tier ? 'next' : 'future';
            const status = statusOf(tier);
            return (
              <Fragment key={tier.perks}>
                <div className="vh-potency-pips" aria-hidden="true">
                  {Array.from({ length: PERKS_PER_TIER - 1 }, (_, p) => (
                    <div key={p} className={`vh-skill-pip${spent > i * PERKS_PER_TIER + p ? ' vh-skill-pip-filled' : ''}`} />
                  ))}
                </div>
                <div className="vh-potency-seal">
                  {flare === i ? <span className="vh-const-node-flare" aria-hidden="true" onAnimationEnd={() => setFlare(null)} /> : null}
                  <button type="button"
                    className={`vh-skill-node vh-skill-node-${NODE_STATE[state]}`}
                    data-testid={`alchemist-potency-${tier.perks}`} data-unlocked={unlocked} data-state={state}
                    aria-label={`${t(tier.title)} · ${status} · ${t('{n} perks', { n: tier.perks })}`}
                    aria-describedby={detail?.index === i ? `potency-detail-${tier.perks}` : undefined}
                    style={{ '--vh-skill-node-accent': cls.accent, '--vh-skill-node-ornament-svg': `url("${cls.ornament}")` }}
                    onMouseEnter={e => { playUI(UI.HOVER, { volume: 0.3 }); show(i, e); }} onMouseLeave={() => setDetail(null)}
                    onFocus={e => show(i, e)} onBlur={() => setDetail(null)}
                    onKeyDown={e => { if (e.key === 'Escape') setDetail(null); }}>
                    <div className="vh-skill-node-inner">
                      <div className="vh-skill-node-ornament" aria-hidden="true" />
                    </div>
                  </button>
                  <div className="vh-potency-seal-label" data-state={state}>{t(tier.title)}</div>
                </div>
              </Fragment>
            );
          })}
        </div>
      </section>
      <AnimatePresence>
        {detail ? (
          <TierTooltip key={detail.index} cls={cls} tier={ALCHEMIST_POTENCY[detail.index]}
            status={statusOf(ALCHEMIST_POTENCY[detail.index])} anchorRect={detail.rect} />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
